export interface PlatformDragBoundaryRect {
    bottom: number;
    left: number;
    right: number;
    top: number;
}

export interface PlatformDragBoundaryInsets {
    bottom?: number;
    left?: number;
    right?: number;
    top?: number;
}

export interface PlatformDragBoundaryResolverContext {
    droppableElements: ReadonlyMap<string, HTMLElement>;
    sourceDroppableId: string;
    viewport: PlatformDragBoundaryRect;
}

export type PlatformDragBoundaryResolver = (
    context: PlatformDragBoundaryResolverContext,
) => PlatformDragBoundaryRect | null;

/**
 * `context` constrains a preview to the shared surface containing every
 * droppable in the current DragDropContext. This preserves cross-column moves
 * while preventing a preview from escaping the board, modal, or list that owns
 * it. More restrictive and custom policies remain available for specialized
 * workflows without weakening the platform default.
 */
export type PlatformDragBoundary = 'context' | 'none' | 'source-droppable' | 'viewport' | PlatformDragBoundaryResolver;

const rectFromDomRect = (rect: DOMRect): PlatformDragBoundaryRect => ({
    bottom: rect.bottom,
    left: rect.left,
    right: rect.right,
    top: rect.top,
});

const getViewportRect = (): PlatformDragBoundaryRect => ({
    bottom: typeof window === 'undefined' ? 0 : window.innerHeight,
    left: 0,
    right: typeof window === 'undefined' ? 0 : window.innerWidth,
    top: 0,
});

const getCommonAncestor = (elements: readonly HTMLElement[]): HTMLElement | null => {
    let candidate: HTMLElement | null = elements[0] ?? null;

    while (candidate && !elements.every((element) => candidate?.contains(element))) {
        candidate = candidate.parentElement;
    }

    return candidate;
};

const getUnionRect = (elements: readonly HTMLElement[]): PlatformDragBoundaryRect | null => {
    const rects = elements.map((element) => rectFromDomRect(element.getBoundingClientRect()));
    const firstRect = rects[0];

    if (!firstRect) {
        return null;
    }

    return rects.slice(1).reduce<PlatformDragBoundaryRect>(
        (union, rect) => ({
            bottom: Math.max(union.bottom, rect.bottom),
            left: Math.min(union.left, rect.left),
            right: Math.max(union.right, rect.right),
            top: Math.min(union.top, rect.top),
        }),
        firstRect,
    );
};

export const intersectDragBoundaryRects = (
    first: PlatformDragBoundaryRect,
    second: PlatformDragBoundaryRect,
): PlatformDragBoundaryRect => {
    const left = Math.max(first.left, second.left);
    const top = Math.max(first.top, second.top);

    return {
        bottom: Math.max(top, Math.min(first.bottom, second.bottom)),
        left,
        right: Math.max(left, Math.min(first.right, second.right)),
        top,
    };
};

export const insetDragBoundaryRect = (
    rect: PlatformDragBoundaryRect,
    insets: number | PlatformDragBoundaryInsets = 0,
): PlatformDragBoundaryRect => {
    const normalized =
        typeof insets === 'number'
            ? { bottom: insets, left: insets, right: insets, top: insets }
            : {
                  bottom: insets.bottom ?? 0,
                  left: insets.left ?? 0,
                  right: insets.right ?? 0,
                  top: insets.top ?? 0,
              };
    const horizontalCenter = (rect.left + rect.right) / 2;
    const verticalCenter = (rect.top + rect.bottom) / 2;

    return {
        bottom: Math.max(verticalCenter, rect.bottom - normalized.bottom),
        left: Math.min(horizontalCenter, rect.left + normalized.left),
        right: Math.max(horizontalCenter, rect.right - normalized.right),
        top: Math.min(verticalCenter, rect.top + normalized.top),
    };
};

export const dragBoundaryRectsEqual = (
    first: PlatformDragBoundaryRect | null,
    second: PlatformDragBoundaryRect | null,
): boolean => {
    if (first === second) {
        return true;
    }

    if (!first || !second) {
        return false;
    }

    return (
        first.bottom === second.bottom &&
        first.left === second.left &&
        first.right === second.right &&
        first.top === second.top
    );
};

export const resolvePlatformDragBoundary = ({
    boundary,
    droppableElements,
    insets,
    sourceDroppableId,
}: {
    boundary: PlatformDragBoundary;
    droppableElements: ReadonlyMap<string, HTMLElement>;
    insets?: number | PlatformDragBoundaryInsets;
    sourceDroppableId: string;
}): PlatformDragBoundaryRect | null => {
    if (boundary === 'none') {
        return null;
    }

    const viewport = getViewportRect();
    const connectedElements = [...droppableElements.values()].filter((element) => element.isConnected);
    let resolved: PlatformDragBoundaryRect | null;

    if (typeof boundary === 'function') {
        resolved = boundary({ droppableElements, sourceDroppableId, viewport });
    } else if (boundary === 'viewport') {
        resolved = viewport;
    } else if (boundary === 'source-droppable') {
        const source = droppableElements.get(sourceDroppableId);
        resolved = source ? rectFromDomRect(source.getBoundingClientRect()) : viewport;
    } else {
        const commonAncestor = getCommonAncestor(connectedElements);
        const documentRoot = typeof document === 'undefined' ? null : document.documentElement;
        const documentBody = typeof document === 'undefined' ? null : document.body;

        if (commonAncestor && commonAncestor !== documentRoot && commonAncestor !== documentBody) {
            resolved = rectFromDomRect(commonAncestor.getBoundingClientRect());
        } else {
            resolved = getUnionRect(connectedElements) ?? viewport;
        }
    }

    if (!resolved) {
        return null;
    }

    return insetDragBoundaryRect(intersectDragBoundaryRects(resolved, viewport), insets);
};
